/**
 * App-Wurzel: React Router + Refine + Stack Auth + MAESTRO-Design-Shell.
 *
 * Routing:
 *   /handler/*                    -> StackHandler (Sign-in/up etc., Stack Auth)
 *   /login                        -> gestylte Login-Seite
 *   /angebot/:token               -> OEFFENTLICHE Angebotsseite (KEIN Login!)
 *   /kundenportal                 -> OEFFENTLICHE Kundenportal-Login-Anfrage (KEIN Login!)
 *   /kundenportal/anmelden        -> OEFFENTLICHER Magic-Link-Callback (KEIN Login!)
 *   alle uebrigen Routen          -> geschuetzt, in der AppShell (Sidebar/Topbar)
 */
import type { ReactNode } from "react";
import { Refine } from "@refinedev/core";
import routerBindings from "@refinedev/react-router-v6";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { StackProvider, StackTheme, StackHandler, useUser } from "@stackframe/react";
import { stackApp } from "./stack";
import { dataProvider } from "./providers/dataProvider";
import { authProvider } from "./providers/authProvider";
import { AppShell } from "./ui/AppShell";
import { MitgliedschaftsGate } from "./ui/MitgliedschaftsGate";
import { CopilotProvider } from "./ui/CopilotProvider";
import { ReferenceProgressProvider } from "./ui/ReferenceProgressProvider";
import { Logo, PageHeader, ToastProvider } from "./ui/components";
import DashboardPage from "./pages/dashboard";
import AssistentPage from "./pages/assistent";
import InquiriesPage from "./pages/inquiries";
import PosteingangPage from "./pages/posteingang";
import BuchungenPage from "./pages/buchungen";
// Punkt 118: Vertrieb > Angebote, alle Angebote ueber alle Kunden.
import AngebotePage from "./pages/angebote";
import EventDetailPage from "./pages/event-detail";
import AbrechnungPage from "./pages/abrechnung";
import BuilderPage from "./pages/builder";
import CustomersPage from "./pages/customers";
import CustomerDetailPage from "./pages/customer-detail";
// Adressmodell PR4 (WARTESCHLANGE Punkt 7): Owner/Admin-Aufraeumflaeche, dasselbe Rollen-Gate wie
// die Dubletten-Kandidaten auf CustomersPage - bewusst NICHT unter pages/admin/, s. Kopfkommentar
// von adressen-ohne-land.tsx.
import AdressenOhneLandPage from "./pages/adressen-ohne-land";
// Punkt 101 Stufe 1: Saison-Vormerkungen der Website, nur lesen.
import VormerkungenPage from "./pages/vormerkungen";
import CatalogPage from "./pages/catalog";
import KnowledgePage from "./pages/knowledge";
import MenuImportPage from "./pages/menu-import";
import PaketePage from "./pages/pakete";
import PaketBearbeitenPage from "./pages/paket-bearbeiten";
import KartenPage from "./pages/karten";
import TasksPage from "./pages/tasks";
import AuswertungPage from "./pages/auswertung";
// AH-7 (docs/KONZEPT-ANALYTICS-HUB.md §3): Business Health Hub — KI-Fazit + Kennzahlen-Kacheln.
import PaymentsPage from "./pages/payments";
// F2.1: die Freigabeliste — was MAESTRO verschicken wuerde, aber ohne Freigabe nicht darf.
import FreigabenPage from "./pages/freigaben";
import MailProtokollPage from "./pages/mail-protokoll";
// C-03-Hinweis (aufgaben.md, WELLE 1): Backoffice-Sicht auf unmatched_provider_events.
import UnmatchedProviderEventsPage from "./pages/unmatched-provider-events";
import CalendarPage from "./pages/calendar";
import SettingsPage from "./pages/settings";
import WidgetEditorPage from "./pages/widget-editor/WidgetEditorPage";
import MeinKontoPage from "./pages/mein-konto";
import PublicOfferPage from "./pages/offer-public";
import PublicRechnungsadressePage from "./pages/public-rechnungsadresse";
import GastAnfragePage from "./pages/gast-anfrage";
import GruppenanfragePage from "./pages/gruppenanfrage";
import PublicChatPage from "./pages/chat-public";
// Kundenkonto P0 (docs/KONZEPT-KUNDENKONTO.md): Magic-Link-Login, KEIN Passwort.
import KundenportalPage from "./pages/kundenportal";
import KundenportalAnmeldenPage from "./pages/kundenportal-anmelden";
// Kundenkonto P1: Vorgangsdetail, sitzungsbasiert.
import KundenportalVorgangPage from "./pages/kundenportal-vorgang";
import { LoginPage, hatEinladungskontext } from "./pages/login";
import AcceptInvitePage from "./pages/accept-invite";
// Admin-Panel P0 (docs/KONZEPT-ADMIN-PANEL.md): eigener Host-Sonderfall, komplett NEBEN der
// normalen Mandanten-Navigation unten - siehe `istAdminHost` weiter unten in dieser Datei.
import AdminIndexPage from "./pages/admin";
// F0 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2): alte /veranstaltungen/...-Adressen
// leiten auf die neuen /vorgaenge/...- bzw. /buchungen-Adressen um.
// F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2): `/anfragen/:id` ist seit dem Loeschen
// von `pages/anfrage-detail.tsx` selbst ein Umleitungs-Resolver auf die eine Vorgangsseite.
import { AnfrageUmleitung, VeranstaltungenUmleitung, WissenUmleitung } from "./ui/umleitungen";

/** Stack-Auth-Handler-Seiten unter /handler/* (Props: app/location/fullPage).
 *  fullPage=true (Default): StackHandler wraped sich selbst in einen eigenen viewport-hohen
 *  Container - noetig fuer Sign-in/up/Passwort-Reset/E-Mail-Verifizierung VOR dem Login (dort
 *  gibt es noch keine AppShell). fullPage=false liefert nur ein Fragment OHNE eigenen Wrapper -
 *  fuer /handler/account-settings INNERHALB der AppShell (Punkt 5, Root-Cause-Fix: fullPage=true
 *  war dort der Grund fuer den "ganz neues Menü"-Effekt beim Klick auf "Zu den
 *  Konto-Einstellungen"). */
export function HandlerRoutes({ fullPage = true }: { fullPage?: boolean }) {
  const location = useLocation();
  // Entscheidung 2 (26.09.2026): Stacks Anmeldeseite zeigt jeden im Projekt aktiven OAuth-Anbieter
  // (auch GitHub) — die Anmeldung laeuft deshalb immer ueber die eigene Seite /login. Alte Links
  // und Stack-Weiterleitungen auf /handler/sign-in behalten dabei `after_auth_return_to`.
  if (location.pathname === "/handler/sign-in") return <Navigate to={`/login${location.search}`} replace />;
  // Entscheidung 08.10.2026 „Nur mit Einladung": Registrieren nur auf dem Weg zur Einladungsannahme.
  if (location.pathname.replace(/\/+$/, "") === "/handler/sign-up" && !hatEinladungskontext(location.search))
    return <Navigate to={`/login${location.search}`} replace />;
  if (!fullPage) return <StackHandler app={stackApp} location={location.pathname} fullPage={false} />;
  return (
    <StackRahmen>
      <StackHandler app={stackApp} location={location.pathname} fullPage={false} />
    </StackRahmen>
  );
}

/** Rahmen der Stack-Seiten VOR dem Login (Registrieren, Passwort vergessen, E-Mail bestaetigen)
 *  wie /login: Foto mit dunklem Verlauf, weisse Karte, MAESTRO-Kachel. */
function StackRahmen({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-margin-mobile py-10">
      <img src="/img/login-v2.webp" alt="" className="absolute inset-0 z-0 h-full w-full object-cover" />
      <div className="absolute inset-0 z-0 bg-[linear-gradient(165deg,rgba(24,19,13,.55),rgba(24,19,13,.78))]" />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-surface-container-lowest p-6 shadow-[0_24px_60px_-12px_rgba(0,0,0,.55)] md:p-8">
        <div className="mb-4 flex items-center justify-center gap-2.5">
          <Logo
            size={30}
            radiusClassName="rounded-[9px]"
            textClassName="text-[17px]"
            mitWortmarke
            wortmarkeClassName="font-body-sm text-[14px] font-bold tracking-[0.22em] text-on-background"
          />
        </div>
        {children}
      </div>
    </div>
  );
}

/** Eingebettete Stack-Auth-Kontoseite (Punkt 5): fullPage=false liefert nur Stack Auths eigene
 *  SidebarLayout (Profile/Sicherheit/Sessions-Tabs, per URL-Hash gewechselt - kein Unterpfad
 *  noetig) als Fragment - hier mit PageHeader + max-width umrahmt, damit es innerhalb der
 *  MAESTRO-AppShell erkennbar "im Rahmen" wirkt statt wie ein Bruch. */
function EmbeddedAccountSettings() {
  return (
    <>
      <PageHeader title="Konto-Einstellungen" subtitle="Verwaltet von Stack Auth: Profil, Sicherheit und aktive Sitzungen." />
      <div className="mx-auto max-w-4xl">
        <HandlerRoutes fullPage={false} />
      </div>
    </>
  );
}

/** Schutz-Layout: leitet nicht authentifizierte Nutzer zur Anmeldung um.
 *  MitgliedschaftsGate liegt darin GANZ AUSSEN (Auffangnetz, 13.08.2026): wer eingeloggt ist,
 *  aber in keinem Betrieb Mitglied (eingeladen, registriert, nie mit `?token=…` angekommen), sah
 *  hier bisher eine Wand aus 403-Fehlerkarten. Das Gate zeigt ihm stattdessen den
 *  Beitritts-Bildschirm — und verhindert, dass Shell und Copilot ihre tenant-gebundenen Abrufe
 *  ueberhaupt starten.
 *  CopilotProvider umschließt AppShell UND den Outlet, sodass die angedockte KI-Chat-Spalte
 *  über Seitenwechsel denselben Verlauf behält (eine geteilte Session).
 *  ReferenceProgressProvider (Automatik P4.2) liegt aus demselben Grund hier: Topbar-Indikator,
 *  Referenz-Seite und Angebots-Builder lesen EINEN Zustand statt jeder seinen eigenen. */
/** Eingeloggt, aber OHNE AppShell/Sidebar (Punkt-5-Korrektur, 20.08.2026): Antoine wollte in der
 *  Vorschau "so sieht der Gast diese Seite" NICHT das interne Website-Navigationsmenü (Sidebar
 *  mit "Anfragen" etc.) sehen — das darf ein Gast nie sehen, und die Vorschau soll 1:1 zeigen,
 *  was unter /angebot/:token tatsaechlich erscheint. MitgliedschaftsGate bleibt (die Vorschau
 *  laedt authentifiziert von /api/events/:id/angebots-vorschau, braucht also Mitgliedschaft),
 *  CopilotProvider/ReferenceProgressProvider/AppShell bewusst NICHT — die Vorschau-Seite selbst
 *  ist "bewusst KEIN Layout-Fork" (Kommentar in offer-public.tsx) und braucht sie nicht. */
function PreviewOnlyLayout() {
  useUser({ or: "redirect" });
  return (
    <MitgliedschaftsGate>
      <Outlet />
    </MitgliedschaftsGate>
  );
}

function ProtectedLayout() {
  useUser({ or: "redirect" });
  return (
    <MitgliedschaftsGate>
      {/* Phase C.2 WP-D1 (Postfach-Zuordnung): `posteingang.tsx` ist die erste Seite im
          normalen Betreiber-Baum, die `useToast()` braucht (Toast nach "Bestätigen &
          antworten"/"Zuordnen & antworten") - bisher umschloss `ToastProvider` NUR den
          Admin-Host (`pages/admin/index.tsx`), hier fehlte er komplett. EINMAL hier, für den
          gesamten geschützten Baum, statt je Seite neu. */}
      <ToastProvider>
        <CopilotProvider>
          <ReferenceProgressProvider>
            <AppShell>
              <Outlet />
            </AppShell>
          </ReferenceProgressProvider>
        </CopilotProvider>
      </ToastProvider>
    </MitgliedschaftsGate>
  );
}

// Stack-Auth-Seiten (Account, Login, Verify, Reset) im MAESTRO-CI: Akzent-Orange + Radius
// passend zur App. Zusammen mit lang="de-DE" oben erscheinen die Stack-Flows on-brand und deutsch
// statt als nacktes englisches Default-CI.
const STACK_THEME = {
  light: { primary: "#C2410C" },
  dark: { primary: "#E2551E" },
  radius: "9px",
};

/**
 * true genau dann, wenn das Label direkt links von der Basis-Domain "maestro" ist (also Host
 * `maestro.<APP_BASE_DOMAIN>`) - derselbe Stil wie die bestehende Slug-Erkennung im Frontend
 * (`lib/membership.ts`, `pages/beitritt.tsx`: `window.location.hostname.split(".")[0]`), nur
 * gegen das feste Label "maestro" statt gegen einen dynamischen Mandanten-Slug geprueft. Die
 * eigentliche, sicherheitsrelevante Pruefung passiert server-seitig exakt (`istAdminHost` in
 * `apps/api/src/routes/admin.ts`, Host === `maestro.<APP_BASE_DOMAIN>`) - dieser Frontend-Check
 * entscheidet nur, welcher Routen-Baum ueberhaupt gerendert wird, kein Backend-Ersatz.
 */
function istAdminHost(): boolean {
  return window.location.hostname.split(".")[0] === "maestro";
}

export default function App() {
  // Admin-Host-Sonderfall (docs/KONZEPT-ADMIN-PANEL.md § Architektur, Punkt 5): ein komplett
  // eigener, kleiner Baum - KEINE Beruehrung von Refine/AppShell/MitgliedschaftsGate, die alle
  // das Ein-Mandant-pro-Request-Modell voraussetzen. Bei falschem Host laeuft die normale
  // Rueckgabe unten unveraendert weiter.
  // Bewusst OHNE Route-Elemente in diesem Zweig, Verzweigung stattdessen per
  // `window.location.pathname`: `apps/web/test/e2e-abdeckung.ts` liest Seiten-Routen woertlich
  // aus den Route-Tags dieser Datei aus und verlangt fuer jede NEU hinzukommende sofort einen
  // E2E-Golden-Path-Spec (kein Ausnahme-Eintrag fuer neue Seiten). Die Admin-Shell haengt am
  // HOST, nicht an einem React-Router-Pfad - ihr eigenes Golden-Path-Kriterium steht als
  // separater, noch offener Punkt in docs/LOOP-ADMIN-PANEL.md § P0 und kommt in der naechsten
  // Iteration. Stack-Auth-Sign-in/-out braucht trotzdem `/handler`-Unterpfade - dafuer reicht
  // hier ein reiner Pfad-Vergleich, weil `HandlerRoutes` selbst schon per `useLocation()` weiss,
  // welche Stack-Unterseite gemeint ist (kein zweites Route-Matching noetig).
  if (istAdminHost()) {
    const istHandlerPfad = window.location.pathname.startsWith("/handler/");
    // urls.signIn ist /login (stack.ts) — auch auf dem Admin-Host, sonst liefe die Anmeldung dort
    // in AdminIndexPage und von dort wieder zur Anmeldung.
    const istLogin = window.location.pathname === "/login";
    return (
      <BrowserRouter>
        <StackProvider app={stackApp} lang="de-DE">
          <StackTheme theme={STACK_THEME}>
            {istHandlerPfad ? <HandlerRoutes /> : istLogin ? <LoginPage /> : <AdminIndexPage />}
          </StackTheme>
        </StackProvider>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <StackProvider app={stackApp} lang="de-DE">
        <StackTheme theme={STACK_THEME}>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            routerProvider={routerBindings}
            resources={[
              { name: "inquiries", list: "/anfragen", meta: { label: "Anfragen" } },
              { name: "events", list: "/buchungen", meta: { label: "Buchungen" } },
              { name: "customers", list: "/kunden", meta: { label: "Kunden" } },
            ]}
            options={{ syncWithLocation: true, disableTelemetry: true }}
          >
            <Routes>
              <Route path="/handler/*" element={<HandlerRoutes />} />
              <Route path="/login" element={<LoginPage />} />
              {/* Einladung annehmen - erzwingt Login, braucht aber KEINE Tenant-Mitgliedschaft (ProtectedLayout waere hier falsch). */}
              <Route path="/einladung" element={<AcceptInvitePage />} />
              {/* Oeffentliche Kundenseite - bewusst ausserhalb des Schutz-Layouts. */}
              <Route path="/angebot/:token" element={<PublicOfferPage />} />
              {/* sql/344 (Antoine 30.08.2026): Rechnungsadresse-Selbstauskunft, Rueckfallweg. */}
              <Route path="/rechnungsadresse/:token" element={<PublicRechnungsadressePage />} />
              {/* Gastseite zur Anfrage (sql/674, Mockup v2 der Eingangsmail, 06.10.2026): Frage oder Korrektur ohne Login. */}
              <Route path="/anfrage/:token" element={<GastAnfragePage />} />
              <Route path="/chat" element={<PublicChatPage />} />
              {/* Saison-Mails (Antoine 26.09.2026): Knopf „Als Gruppe anfragen“ — ohne Login, ab 10 Personen eine Anfrage. */}
              <Route path="/gruppenanfrage" element={<GruppenanfragePage />} />
              {/* Kundenkonto P0 (docs/KONZEPT-KUNDENKONTO.md): Login-Anfrage-Seite + Link-Callback,
                  beide bewusst ausserhalb des Schutz-Layouts (kein Login noetig zum Aufrufen). */}
              <Route path="/kundenportal" element={<KundenportalPage />} />
              <Route path="/kundenportal/anmelden" element={<KundenportalAnmeldenPage />} />
              {/* Kundenkonto P1: Vorgangsdetail (nur der eingeloggte Kunde, session-gescoped). */}
              <Route path="/kundenportal/vorgang/:id" element={<KundenportalVorgangPage />} />
              {/* Kundenseiten-Vorschau (05.08.2026, Punkt-5-Korrektur 20.08.2026): DIESELBE
                  Komponente wie /angebot/:token, aber eingeloggt und aus GET
                  /api/events/:id/angebots-vorschau - der Betreiber sieht die Gast-Seite VOR dem
                  Versand (Annehmen/Absagen dort deaktiviert). Bewusst NICHT unter ProtectedLayout:
                  keine AppShell-Sidebar, damit die Vorschau 1:1 zeigt, was der Gast sieht. */}
              <Route element={<PreviewOnlyLayout />}>
                {/* Bewusst ALT und NIE umzuziehen (F0, lib/pfade.ts#angebotsseitePfad): diese
                    Adresse steht bereits in versendeten Kunden-Mails (routes/offers.ts). Als
                    spezifischere Route registriert VOR der Splat-Umleitung `/veranstaltungen/*`
                    unten — React Router matcht sie deshalb zuerst (s. ui/umleitungen.tsx). */}
                <Route path="/veranstaltungen/:id/angebotsseite" element={<PublicOfferPage vorschau />} />
              </Route>
              <Route element={<ProtectedLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="/assistent" element={<AssistentPage />} />
                <Route path="/anfragen" element={<InquiriesPage />} />
                {/* F2: kein eigenes Detail mehr — reiner Umleitungs-Resolver auf die eine
                    Vorgangsseite (s. Kopfkommentar an `AnfrageUmleitung`). */}
                <Route path="/anfragen/:id" element={<AnfrageUmleitung />} />
                <Route path="/posteingang" element={<PosteingangPage />} />
                <Route path="/angebote" element={<AngebotePage />} />
                <Route path="/buchungen" element={<BuchungenPage />} />
                <Route path="/vorgaenge/:id" element={<EventDetailPage />} />
                <Route path="/vorgaenge/:id/abrechnung" element={<AbrechnungPage />} />
                <Route path="/vorgaenge/:id/angebot" element={<BuilderPage />} />
                {/* F0: alte /veranstaltungen/...-Adressen leiten auf die neuen /vorgaenge/...-
                    bzw. /buchungen-Adressen um (Query-String erhalten). Ausnahme
                    /veranstaltungen/:id/angebotsseite oben, spezifischer registriert. */}
                <Route path="/veranstaltungen/*" element={<VeranstaltungenUmleitung />} />
                <Route path="/kalender" element={<CalendarPage />} />
                <Route path="/kunden" element={<CustomersPage />} />
                <Route path="/kunden/adressen-ohne-land" element={<AdressenOhneLandPage />} />
                <Route path="/kunden/vormerkungen" element={<VormerkungenPage />} />
                <Route path="/kunden/:id" element={<CustomerDetailPage />} />
                <Route path="/aufgaben" element={<TasksPage />} />
                <Route path="/auswertung" element={<AuswertungPage />} />
                <Route path="/katalog" element={<CatalogPage />} />
                <Route path="/katalog/import" element={<MenuImportPage />} />
                <Route path="/katalog/pakete" element={<PaketePage />} />
                <Route path="/katalog/pakete/:id" element={<PaketBearbeitenPage />} />
                <Route path="/katalog/karten" element={<KartenPage />} />
                <Route path="/gedaechtnis" element={<KnowledgePage />} />
                <Route path="/wissen" element={<WissenUmleitung />} />
                <Route path="/zahlungen" element={<PaymentsPage />} />
                <Route path="/freigaben" element={<FreigabenPage />} />
                <Route path="/mail-protokoll" element={<MailProtokollPage />} />
                <Route path="/ungeklaerte-zahlungen" element={<UnmatchedProviderEventsPage />} />
                <Route path="/einstellungen" element={<SettingsPage />} />
                {/* Widget-Editor (Umbau Drawer -> eigene Route, HANDOFF-2026-08-18-UX-WIDGET-EDITOR.md):
                    "Widget anlegen" in widgets-tab.tsx legt sofort einen Draft an und leitet hierher weiter. */}
                <Route path="/einstellungen/widgets/:id" element={<WidgetEditorPage />} />
                {/* Punkt 5 (Root-Cause-Fix): eingebettete Stack-Auth-Kontoseite INNERHALB der
                    AppShell. React Router v6 matcht diese statische, spezifischere Route
                    automatisch vor dem generischen /handler/*-Fallback oben (Ranking-Algorithmus,
                    unabhaengig von der Deklarationsreihenfolge) - trotzdem der Klarheit halber
                    hier bei den anderen geschuetzten Routen platziert. */}
                <Route path="/handler/account-settings" element={<EmbeddedAccountSettings />} />
                <Route path="/mein-konto" element={<MeinKontoPage />} />
              </Route>
            </Routes>
          </Refine>
        </StackTheme>
      </StackProvider>
    </BrowserRouter>
  );
}
